import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { TimerSettings } from '@/hooks/timer/useTimer';
import SidePanel from './ui/SidePanel';
import { useTheme } from '@/context/ThemeContext';
import { Moon, Sun, Monitor, Timer, Coffee, Sofa, RotateCcw, Volume2, Palette } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AudioSettings from './settings/AudioSettings';
import { audioManager, AudioSettings as AudioSettingsType } from '@/utils/audioManager';
import { getUserBackgroundPreference, setUserBackgroundPreference } from '@/hooks/usePreferredBackground';
import { useAuth } from '@/context/AuthContext';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: TimerSettings;
  onSave: (settings: TimerSettings) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSave,
}) => {
  const [localSettings, setLocalSettings] = useState<TimerSettings>(settings);
  const [audioSettings, setAudioSettings] = useState<AudioSettingsType>(audioManager.getSettings());
  const { theme, setTheme } = useTheme();
  const { user, profile } = useAuth();

  // Authenticated state
  const authenticated = Boolean(user && profile);

  // Local UI state, initialize from profile for authed, 'image' for guest
  const [backgroundPref, setBackgroundPref] = useState(
    getUserBackgroundPreference(profile, authenticated)
  );

  // Listen for profile/background change
  useEffect(() => {
    setBackgroundPref(getUserBackgroundPreference(profile, authenticated));
  }, [profile, authenticated]);

  // Auto-save timer settings when they change
  useEffect(() => {
    // Only save if settings have actually changed from the initial settings
    const hasChanged = JSON.stringify(localSettings) !== JSON.stringify(settings);
    if (hasChanged) {
      // Validate input values
      const { pomodoro, shortBreak, longBreak } = localSettings;
      if (pomodoro > 0 && shortBreak > 0 && longBreak > 0) {
        onSave(localSettings);
        toast.success("Timer settings saved!");
      }
    }
  }, [localSettings, onSave, settings]);

  // Auto-save audio settings when they change
  useEffect(() => {
    audioManager.updateSettings(audioSettings);
    toast.success("Audio settings saved!");
  }, [audioSettings]);

  // Only persist background preference for logged-in users
  useEffect(() => {
    if (authenticated) {
      setUserBackgroundPreference(backgroundPref, user, authenticated);
    }
    // Guests: do nothing, preference not stored
  }, [backgroundPref, authenticated, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Allow empty string during editing
    if (value === '') {
      setLocalSettings((prev) => ({
        ...prev,
        [name]: 0,
      }));
      return;
    }
    
    // Only allow numbers
    if (!/^\d+$/.test(value)) {
      return;
    }
    
    const numValue = parseInt(value);
    
    // Validate input values
    if (numValue <= 0) {
      toast.error("Values must be greater than 0");
      return;
    }

    setLocalSettings((prev) => ({
      ...prev,
      [name]: numValue,
    }));
  };

  const handleToggleChange = (checked: boolean) => {
    setLocalSettings((prev) => ({
      ...prev,
      autoRepeat: checked,
    }));
  };

  const handleAudioSettingsChange = (newAudioSettings: Partial<AudioSettingsType>) => {
    setAudioSettings(prev => ({ ...prev, ...newAudioSettings }));
  };

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title="Settings">
      <div className="px-6 py-4">
        <Tabs defaultValue="timer" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="timer" className="flex items-center gap-2">
              <Timer size={16} />
              Timer
            </TabsTrigger>
            <TabsTrigger value="audio" className="flex items-center gap-2">
              <Volume2 size={16} />
              Audio
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex items-center gap-2">
              <Palette size={16} />
              Theme
            </TabsTrigger>
          </TabsList>

          <TabsContent value="timer" className="space-y-6 mt-6">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Timer</h3>
              
              <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <Timer size={20} />
                  <span className="text-lg">Pomodoro</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    name="pomodoro"
                    value={localSettings.pomodoro || ''}
                    onChange={handleChange}
                    className="w-24 border-gray-300 dark:border-gray-600 focus:border-black focus:ring-black rounded-md text-center"
                    placeholder="25"
                  />
                  <span className="text-lg">Min</span>
                </div>
              </div>

              <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <Coffee size={20} />
                  <span className="text-lg">Short break</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    name="shortBreak"
                    value={localSettings.shortBreak || ''}
                    onChange={handleChange}
                    className="w-24 border-gray-300 dark:border-gray-600 focus:border-black focus:ring-black rounded-md text-center"
                    placeholder="5"
                  />
                  <span className="text-lg">Min</span>
                </div>
              </div>

              <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <Sofa size={20} />
                  <span className="text-lg">Long break</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    name="longBreak"
                    value={localSettings.longBreak || ''}
                    onChange={handleChange}
                    className="w-24 border-gray-300 dark:border-gray-600 focus:border-black focus:ring-black rounded-md text-center"
                    placeholder="30"
                  />
                  <span className="text-lg">Min</span>
                </div>
              </div>

              <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <RotateCcw size={20} />
                  <span className="text-lg">Auto repeat sequence</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={localSettings.autoRepeat}
                    onCheckedChange={handleToggleChange}
                    className="data-[state=checked]:bg-black dark:data-[state=checked]:bg-white"
                  />
                  <span className="text-lg">{localSettings.autoRepeat ? 'On' : 'Off'}</span>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="audio" className="mt-6">
            <h3 className="text-lg font-medium mb-6">Audio</h3>
            <AudioSettings 
              settings={audioSettings}
              onSettingsChange={handleAudioSettingsChange}
            />
          </TabsContent>

          <TabsContent value="appearance" className="space-y-6 mt-6">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Theme</h3>
              <RadioGroup 
                value={theme} 
                onValueChange={(value) => {
                  // Only allow saving for auth users
                  if (authenticated) setTheme(value as any);
                }}
                className="space-y-4"
              >
                <Label htmlFor="light" className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-2 -mx-2">
                  <div className="flex items-center gap-3">
                    <Sun size={20} />
                    <span className="text-lg">Light</span>
                  </div>
                  <RadioGroupItem value="light" id="light" />
                </Label>
                <Label htmlFor="dark" className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-2 -mx-2">
                  <div className="flex items-center gap-3">
                    <Moon size={20} />
                    <span className="text-lg">Dark</span>
                  </div>
                  <RadioGroupItem value="dark" id="dark" />
                </Label>
                <Label htmlFor="system" className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-2 -mx-2">
                  <div className="flex items-center gap-3">
                    <Monitor size={20} />
                    <span className="text-lg">System</span>
                  </div>
                  <RadioGroupItem value="system" id="system" />
                </Label>
              </RadioGroup>
              {!authenticated && (
                <div className="text-xs text-gray-500 mt-2 ml-1">
                  Sign in to customize your theme and have your preference remembered.
                </div>
              )}
            </div>
            <div className="space-y-6">
              <h3 className="text-lg font-medium mt-6">Timer Background Style</h3>
              <div className="space-y-3">
                <label className="flex items-center cursor-pointer gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all">
                  <input
                    type="radio"
                    name="backgroundPref"
                    checked={backgroundPref === 'image'}
                    onChange={() => authenticated && setBackgroundPref('image')}
                    className="accent-black dark:accent-white mr-2"
                    disabled={!authenticated}
                  />
                  <span>Unsplash Images <span className="text-xs text-gray-500">(default)</span></span>
                </label>
                <label className="flex items-center cursor-pointer gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all">
                  <input
                    type="radio"
                    name="backgroundPref"
                    checked={backgroundPref === 'gradient'}
                    onChange={() => authenticated && setBackgroundPref('gradient')}
                    className="accent-black dark:accent-white mr-2"
                    disabled={!authenticated}
                  />
                  <span>Time-of-Day Gradients</span>
                </label>
                <div className="text-xs text-gray-500 ml-6 mb-2">
                  {authenticated
                    ? 'Gradients are a good fallback when Unsplash images can\'t load or to minimize distraction.'
                    : 'Sign in to change the background type and have it remembered.'}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </SidePanel>
  );
};

export default SettingsModal;
